/**
 * Mock data pour le CreatorsPanel — initial state du `creatorsAtom`.
 *
 * Sera remplace par un fetch sur le control plane quand l'auth + Convex
 * seront branches (cf project_convex_control_plane_2026_05_05).
 *
 * Note : le TeamPanel lit aussi `creatorsAtom` — toute modification ici se
 * propage aux selects de creators dans Team (multi-select scope d'access).
 */

export type CreatorStatus = "active" | "paused" | "pending";

/**
 * Configuration des notifications de commandes envoyees vers le
 * `notification_chat_id` (chat Telegram). Shape alignee avec
 * `creator_notification_configs` cote Neon.
 */
export interface NotificationPrefs {
  is_enabled: boolean;
  notify_on_created: boolean;
  notify_on_paid: boolean;
  notify_on_cancelled: boolean;
  notify_on_delivered: boolean;
}

export const DEFAULT_NOTIFICATIONS: NotificationPrefs = {
  is_enabled: true,
  notify_on_created: true,
  notify_on_paid: true,
  notify_on_cancelled: true,
  notify_on_delivered: false,
};

export interface Creator {
  id: string;
  /** Slug Convex — cle de jointure stable avec les autres data planes (Cyrano). */
  slug: string;
  name: string;
  /** Couleur d'accent pour le card stripe + avatar fallback + chips. */
  color: string;
  telegram_handle?: string;
  description?: string;
  /**
   * Status runtime de l'agent — `active` quand le bot tourne, `paused`
   * sinon. Pas configurable dans le modal d'edition (c'est un etat derive,
   * pas une preference). Affiche en read-only sur la card.
   */
  status: CreatorStatus;
  /**
   * Photo de profil — data URL base64, redimensionnee en 256x256 max avant
   * stockage. Si non definie : fallback avatar = couleur + initiale du nom.
   * Sera remplace par un upload S3/CDN quand le backend sera la.
   */
  avatar_url?: string;

  /* ------------------------------------------------------------------ */
  /* Connection — config Telegram bot                                    */
  /* ------------------------------------------------------------------ */
  /**
   * Telegram Bot API token (ex: "7304140728:AA...xxx"). Stocke en clair
   * dans localStorage tant que le backend n'est pas la — sera chiffre
   * cote serveur a terme. Affiche masque dans le modal.
   */
  bot_token?: string;
  /** Auto-resolu au save dans le vrai backend. Mock ici. */
  bot_username?: string;
  bot_first_name?: string;
  /**
   * Etat runtime du bot Telegram. `true` = bot actif (repond aux messages),
   * `false` = bot en pause (webhook retourne 200 sans traiter), `null` = pas
   * encore d'integration configuree. Distinct de `status` UI qui aggrege
   * aussi l'archive Convex.
   */
  bot_is_enabled?: boolean | null;
  /**
   * Sante REELLE de la connexion Telegram Business (source de verite
   * business_connections). Distincte de `bot_is_enabled`/pause utilisateur :
   * une connexion peut etre `expired` (Premium expire) alors que le bot est
   * "enabled". Cf PLAN_CYRANO_BCID_ROBUSTNESS.md. `null` = backend muet.
   */
  connection_health?:
    | "connected"
    | "degraded"
    | "expired"
    | "never-connected"
    | null;

  /* ------------------------------------------------------------------ */
  /* Vault — groupe Telegram Forum ou les medias sont stockes            */
  /* ------------------------------------------------------------------ */
  vault_chat_id?: number;
  vault_chat_name?: string;

  /* ------------------------------------------------------------------ */
  /* Notifications — chat de destination + types actifs                  */
  /* ------------------------------------------------------------------ */
  notification_chat_id?: number;
  notification_chat_name?: string;
  notifications?: NotificationPrefs;

  /** Revenue cumule en cents USD (lifetime, paid + delivered orders). */
  revenue_cents: number;
  /** Nombre de ventes (lifetime, paid + delivered orders). */
  sales_count: number;
  /** Nombre total de messages echangés (toutes directions, toutes conv). */
  messages_count: number;
  /** Nombre de fans actifs sur 30 jours (DISTINCT user_id incoming). */
  fans_count: number;
  added_at: number;
  /**
   * La NATURE de la ligne `creators` : une créatrice (défaut) ou un SITE — un
   * espace de comptes X sans Telegram ni persona (cumgram, un collaborateur…).
   * Porté ici depuis le 20-09-2026 pour que l'app Twitter puisse lister ses
   * ESPACES depuis une seule liste, sans redemander Convex.
   */
  kind?: "creator" | "site";
}

/** Format compact pour les revenues : 52_300_00 cents → "$52.3K". */
export function formatRevenue(cents: number): string {
  const dollars = cents / 100;
  if (dollars >= 1_000_000) return `$${(dollars / 1_000_000).toFixed(1)}M`;
  if (dollars >= 1_000) return `$${(dollars / 1_000).toFixed(1)}K`;
  return `$${dollars.toFixed(0)}`;
}

export function formatFans(count: number): string {
  if (count >= 1_000_000) return `${(count / 1_000_000).toFixed(1)}M`;
  if (count >= 1_000) return `${(count / 1_000).toFixed(1)}K`;
  return count.toLocaleString();
}

/** Palette de couleurs proposees au creator picker — 12 swatches. */
export const COLOR_PALETTE: string[] = [
  "#ef4444", // red
  "#f59e0b", // amber
  "#eab308", // yellow
  "#84cc16", // lime
  "#10b981", // emerald
  "#06b6d4", // cyan
  "#3b82f6", // blue
  "#6366f1", // indigo
  "#8b5cf6", // violet
  "#ec4899", // pink
  "#737373", // neutral
  "#a3a3a3", // light neutral
];

export const STATUS_LABELS: Record<CreatorStatus, string> = {
  active: "Active",
  paused: "Paused",
  pending: "Pending",
};
